// components/Admin/InvoicePDF.js
import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';

const C = {
  dark: '#123249',
  mid: '#2D5A74',
  primary: '#447793',
  light: '#DDE8ED',
  white: '#ffffff',
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: C.dark,
    backgroundColor: C.white,
  },

  /* ── HEADER ── */
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 2,
    borderBottomColor: C.primary,
    paddingBottom: 10,
    marginBottom: 14,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '60%',
  },
  logo: {
    width: 50,
    height: 50,
    marginRight: 10,
    objectFit: 'contain',
  },
  companyName: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    marginBottom: 2,
  },
  companySubName: {
    fontSize: 9,
    color: C.mid,
    marginBottom: 2,
  },
  companyAddress: {
    fontSize: 8,
    color: C.mid,
    marginBottom: 1,
  },
  companyMeta: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    marginTop: 3,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  invoiceTitle: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    letterSpacing: 1,
  },
  invoiceNumber: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: C.primary,
    marginTop: 4,
  },

  /* ── BILL TO + DATES ROW ── */
  billDatesRow: {
    flexDirection: 'row',
    marginBottom: 14,
  },
  billToBox: {
    width: '55%',
    paddingRight: 15,
  },
  billToLabel: {
    fontSize: 8,
    color: C.mid,
    marginBottom: 2,
  },
  billToName: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    marginBottom: 3,
  },
  billToText: {
    fontSize: 8,
    color: C.mid,
    marginBottom: 1,
    lineHeight: 1.4,
  },
  datesBox: {
    width: '45%',
  },
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    alignItems: 'center',
  },
  dateLabel: {
    fontSize: 8,
    color: C.mid,
    width: '45%',
  },
  dateValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    width: '55%',
    textAlign: 'right',
  },

  /* ── ITEMS TABLE ── */
  table: {
    marginBottom: 0,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: C.light,
    paddingVertical: 5,
    paddingHorizontal: 4,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: C.light,
    paddingVertical: 5,
    paddingHorizontal: 4,
    minHeight: 28,
  },
  tableRowAlt: {
    backgroundColor: '#f9fbfc',
  },
  thText: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
  },
  tdText: {
    fontSize: 8,
    color: C.mid,
  },
  tdBold: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
  },
  c1:  { width: '5%',  textAlign: 'center' },
  c2:  { width: '26%', textAlign: 'left'   },
  c3:  { width: '10%', textAlign: 'center' },
  c4:  { width: '7%',  textAlign: 'center' },
  c5:  { width: '12%', textAlign: 'right'  },
  c6:  { width: '8%',  textAlign: 'center' },
  c7:  { width: '8%',  textAlign: 'center' },
  c8:  { width: '8%',  textAlign: 'center' },
  c9:  { width: '16%', textAlign: 'right'  },

  /* ── TOTALS AREA ── */
  totalsArea: {
    flexDirection: 'row',
    marginTop: 6,
    marginBottom: 14,
  },
  totalsLeft: {
    width: '55%',
    paddingRight: 15,
  },
  totalsRight: {
    width: '45%',
    borderWidth: 1,
    borderColor: C.light,
    borderRadius: 3,
    overflow: 'hidden',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: C.light,
  },
  totalRowFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: C.dark,
  },
  totalLabel: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
  },
  totalValue: {
    fontSize: 9,
    color: C.mid,
  },
  totalLabelFinal: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: C.white,
  },
  totalValueFinal: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: C.white,
  },

  /* Words */
  wordsSection: {
    marginBottom: 10,
  },
  wordsLabel: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    marginBottom: 3,
  },
  wordsText: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    borderBottomWidth: 1,
    borderBottomColor: C.primary,
    paddingBottom: 4,
  },

  /* Bank Details */
  bankSection: {
    marginBottom: 10,
  },
  bankTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    marginBottom: 3,
  },
  bankText: {
    fontSize: 8,
    color: C.mid,
    marginBottom: 1,
  },

  /* ── NOTE + QR ── */
  noteQrRow: {
    flexDirection: 'row',
    marginBottom: 12,
    alignItems: 'flex-start',
  },
  noteBox: {
    flex: 1,
    backgroundColor: C.light,
    borderLeftWidth: 3,
    borderLeftColor: C.primary,
    padding: 10,
    marginRight: 15,
    borderRadius: 2,
  },
  noteLabel: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    marginBottom: 4,
  },
  noteText: {
    fontSize: 8,
    color: C.mid,
    lineHeight: 1.5,
  },
  qrBox: {
    width: 80,
    alignItems: 'center',
  },
  qrImage: {
    width: 75,
    height: 75,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: C.primary,
  },
  qrPlaceholder: {
    width: 75,
    height: 75,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: C.primary,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f9fb',
  },
  qrPlaceholderText: {
    fontSize: 7,
    color: C.mid,
    textAlign: 'center',
    marginTop: 4,
  },

  /* ── TERMS ── */
  termsSection: {
    marginBottom: 14,
  },
  termsTitle: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: C.dark,
    marginBottom: 3,
  },
  termsText: {
    fontSize: 7,
    color: C.mid,
    lineHeight: 1.5,
  },

  /* ── FOOTER ── */
  footer: {
    backgroundColor: C.dark,
    padding: 10,
    borderRadius: 4,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: C.white,
    textAlign: 'center',
    marginBottom: 2,
  },
  footerAddress: {
    fontSize: 7,
    color: C.light,
    textAlign: 'center',
  },
});

// ─── PDF Document ────────────────────────────────────────────────────────────
const InvoicePDFDocument = ({ data, companyDetails, formatDate }) => {
  const logoSrc = companyDetails.logoData || companyDetails.logo || null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* ── HEADER ── */}
        <View style={styles.header} fixed>
          <View style={styles.headerLeft}>
            {logoSrc && <Image src={logoSrc} style={styles.logo} />}
            <View>
              <Text style={styles.companyName}>{companyDetails.companyName}</Text>
              {companyDetails.subName ? (
                <Text style={styles.companySubName}>{companyDetails.subName}</Text>
              ) : null}
              <Text style={styles.companyAddress}>{companyDetails.addressLine1}</Text>
              <Text style={styles.companyAddress}>{companyDetails.addressLine2}</Text>
              <Text style={styles.companyMeta}>GSTIN- {companyDetails.gstin}</Text>
              <Text style={styles.companyMeta}>MSME- {companyDetails.msme}</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.invoiceTitle}>TAX INVOICE</Text>
            <Text style={styles.invoiceNumber}>Invoice# {data.invoiceNumber}</Text>
          </View>
        </View>

        {/* ── BILL TO + DATES ── */}
        <View style={styles.billDatesRow}>
          <View style={styles.billToBox}>
            <Text style={styles.billToLabel}>Bill To:</Text>
            <Text style={styles.billToName}>{data.clientName || '____________________'}</Text>
            <Text style={styles.billToText}>
              {(data.clientAddress || '____________________').replace(/\n/g, ', ')}
            </Text>
            <Text style={styles.billToText}>GSTIN - {data.clientGST || '____________________'}</Text>
            <Text style={styles.billToText}>Place of Supply: {data.placeOfSupply || '____________________'}</Text>
          </View>
          <View style={styles.datesBox}>
            <View style={styles.dateRow}>
              <Text style={styles.dateLabel}>Invoice Date :</Text>
              <Text style={styles.dateValue}>{formatDate(data.invoiceDate)}</Text>
            </View>
            <View style={styles.dateRow}>
              <Text style={styles.dateLabel}>Due Date :</Text>
              <Text style={styles.dateValue}>{formatDate(data.dueDate)}</Text>
            </View>
          </View>
        </View>

        {/* ── ITEMS TABLE ── */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader} fixed>
            <Text style={[styles.thText, styles.c1]}>#</Text>
            <Text style={[styles.thText, styles.c2]}>Item Description</Text>
            <Text style={[styles.thText, styles.c3]}>HSN/SAC</Text>
            <Text style={[styles.thText, styles.c4]}>Qty.</Text>
            <Text style={[styles.thText, styles.c5]}>Rate</Text>
            <Text style={[styles.thText, styles.c6]}>SGST</Text>
            <Text style={[styles.thText, styles.c7]}>CGST</Text>
            <Text style={[styles.thText, styles.c8]}>Cess</Text>
            <Text style={[styles.thText, styles.c9]}>Amount</Text>
          </View>

          {/* Table Rows — each row is individually breakable, no group wrapping */}
          {data.items.map((item, index) => (
            <View
              key={index}
              style={[
                styles.tableRow,
                index % 2 !== 0 ? styles.tableRowAlt : null,
              ]}
              wrap={false}
            >
              <Text style={[styles.tdText, styles.c1]}>{index + 1}</Text>
              <Text style={[styles.tdBold, styles.c2]}>{item.description || '—'}</Text>
              <Text style={[styles.tdText, styles.c3]}>{item.hsn || '—'}</Text>
              <Text style={[styles.tdText, styles.c4]}>{item.qty}</Text>
              <Text style={[styles.tdText, styles.c5]}>
                ₹{parseFloat(item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Text>
              <Text style={[styles.tdText, styles.c6]}>{item.sgst}%</Text>
              <Text style={[styles.tdText, styles.c7]}>{item.cgst}%</Text>
              <Text style={[styles.tdText, styles.c8]}>{item.cess}%</Text>
              <Text style={[styles.tdBold, styles.c9]}>
                ₹{parseFloat(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Text>
            </View>
          ))}
        </View>

        {/* ── TOTALS AREA — kept together, won't split across pages ── */}
        <View style={styles.totalsArea} wrap={false}>
          {/* Left: words + bank */}
          <View style={styles.totalsLeft}>
            <View style={styles.wordsSection}>
              <Text style={styles.wordsLabel}>Amount Chargeable (in words):</Text>
              <Text style={styles.wordsText}>
                {data.amountInWords ? `INR ${data.amountInWords.toUpperCase()}.` : '____________________'}
              </Text>
            </View>
            <View style={styles.bankSection}>
              <Text style={styles.bankTitle}>Company's Bank Details</Text>
              <Text style={styles.bankText}>Bank Name : {companyDetails.bankName}</Text>
              <Text style={styles.bankText}>A/c No. : {companyDetails.accountNo}</Text>
              <Text style={styles.bankText}>Branch & IFS Code : {companyDetails.branchIfsc}</Text>
              <Text style={styles.bankText}>for {companyDetails.accountHolder}</Text>
            </View>
          </View>

          {/* Right: totals box */}
          <View style={styles.totalsRight}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Sub Total</Text>
              <Text style={styles.totalValue}>
                ₹{parseFloat(data.subTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} (pre-GST)
              </Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>
                SGST ({data.items[0]?.sgst || 0}%)
              </Text>
              <Text style={styles.totalValue}>
                ₹{parseFloat(data.sgstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>
                CGST ({data.items[0]?.cgst || 0}%)
              </Text>
              <Text style={styles.totalValue}>
                ₹{parseFloat(data.cgstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Text>
            </View>
            {parseFloat(data.cessAmount || 0) > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Cess</Text>
                <Text style={styles.totalValue}>
                  ₹{parseFloat(data.cessAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Text>
              </View>
            )}
            <View style={styles.totalRowFinal}>
              <Text style={styles.totalLabelFinal}>TOTAL</Text>
              <Text style={styles.totalValueFinal}>
                ₹{parseFloat(data.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                {'\n'}(post-GST)
              </Text>
            </View>
          </View>
        </View>

        {/* ── NOTE + QR — kept together ── */}
        <View style={styles.noteQrRow} wrap={false}>
          <View style={styles.noteBox}>
            <Text style={styles.noteLabel}>Note</Text>
            <Text style={styles.noteText}>
              {data.note || 'Kindly note that this invoice for Services Provided remains unpaid. We request at least 50% payment to avoid service interruptions.'}
            </Text>
          </View>
          <View style={styles.qrBox}>
            {companyDetails.qrCodeData ? (
              <Image src={companyDetails.qrCodeData} style={styles.qrImage} />
            ) : (
              <View style={styles.qrPlaceholder}>
                <Text style={{ fontSize: 8, color: C.mid }}>📱</Text>
                <Text style={styles.qrPlaceholderText}>Scan to Pay</Text>
              </View>
            )}
          </View>
        </View>

        {/* ── TERMS — kept together ── */}
        <View style={styles.termsSection} wrap={false}>
          <Text style={styles.termsTitle}>Terms &amp; Conditions</Text>
          <Text style={styles.termsText}>
            <Text style={[styles.termsTitle]}>Declaration</Text>
            {': This invoice reflects the true price of the professional service provided. We affirm compliance with MSME regulations. Payment should be made within the MSME stipulated timeframe; any delay will incur interest per the MSME Act. Disputes shall be resolved under Nagpur jurisdiction. We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.'}
          </Text>
        </View>

        {/* ── FOOTER — kept together ── */}
        <View style={styles.footer} wrap={false}>
          <Text style={styles.footerText}>
            Contact Us: {companyDetails.contact} | Email: {companyDetails.email}
          </Text>
          <Text style={styles.footerAddress}>
            {companyDetails.addressLine1} {companyDetails.addressLine2}
          </Text>
        </View>

      </Page>
    </Document>
  );
};

export default InvoicePDFDocument;
